import { isNetworkCreateError } from '@/utils/http/networkCreateError';
import { logger } from '@/utils/logger/logger';
import {
  finishTracking,
  type SessionFlowFailure,
  startTracking,
  type TrackingHandle,
  TrackingKey,
} from '@heidi/runtime/performance';

// `document_update` client metrics: one attempt per autosave request, after the
// debounce has coalesced the edits. Autosave is autonomous, so no intent.
// A queued save the editor drops before sending is not an attempt.
// A newer save never aborts an older one, so each closes with its own outcome.
// Not yet covered: saves paused while the browser reports offline. The mutation
// waits for reconnect before the attempt opens; opening it in `onMutate` is the
// follow-up.

/** The editor whose autosave sent the request. Diagnostic only: RUM context. */
export type DocumentAutosaveEditor =
  | 'markdown_native'
  | 'block_editor_html'
  | 'block_editor_markdown';

export type DocumentUpdateAttempt = { editor: DocumentAutosaveEditor };

export type DocumentUpdateErrorName =
  | 'DocumentUpdateNetworkUnavailable'
  | 'DocumentUpdateTimeout'
  | 'DocumentUpdateServerError'
  | 'DocumentUpdateRejected'
  | 'DocumentUpdateForbidden'
  | 'DocumentUpdateInterrupted'
  | 'DocumentUpdateUnclassified';

/**
 * The `document_update` failure vocabulary, registered with the rest of the
 * session-flow names. `DocumentUpdateUnclassified` is absent on purpose, so it
 * lands in `_OTHER`.
 */
export const DOCUMENT_UPDATE_FAILURE_TYPES: Readonly<
  Record<
    Exclude<DocumentUpdateErrorName, 'DocumentUpdateUnclassified'>,
    SessionFlowFailure
  >
> = {
  DocumentUpdateNetworkUnavailable: {
    outcome: 'failed',
    errorType: 'network_unavailable',
  },
  DocumentUpdateTimeout: { outcome: 'failed', errorType: 'request_timeout' },
  DocumentUpdateServerError: { outcome: 'failed', errorType: 'server_error' },
  DocumentUpdateRejected: { outcome: 'failed', errorType: 'request_rejected' },
  // Lost access to the session: a precondition, not a save fault.
  DocumentUpdateForbidden: { outcome: 'blocked', errorType: 'permission_denied' },
  // Session expiry or logout ended the save, not the save itself.
  DocumentUpdateInterrupted: { outcome: 'cancelled' },
};

const namedError = (name: DocumentUpdateErrorName): Error => {
  const error = new Error(name);
  error.name = name;
  return error;
};

/**
 * Maps a save rejection to a bounded name; an unknown cause lands in `_OTHER`.
 * The raw value is never forwarded: its name may mean something else for
 * another operation in the shared name map.
 */
export const classifyDocumentUpdateError = (error: unknown): Error => {
  const { code, response } = (error ?? {}) as {
    code?: unknown;
    response?: { status?: unknown };
  };

  const status = response?.status;
  if (typeof status === 'number') {
    if (status === 401) return namedError('DocumentUpdateInterrupted');
    if (status === 403) return namedError('DocumentUpdateForbidden');
    if (status === 408 || status === 504) return namedError('DocumentUpdateTimeout');
    if (status >= 500) return namedError('DocumentUpdateServerError');
    if (status >= 400) return namedError('DocumentUpdateRejected');
  }

  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return namedError('DocumentUpdateTimeout');
  }
  // Desktop offline short-circuits the request as an axios `ERR_NETWORK`.
  if (code === 'ERR_NETWORK' || isNetworkCreateError(error)) {
    return namedError('DocumentUpdateNetworkUnavailable');
  }
  return namedError('DocumentUpdateUnclassified');
};

// Telemetry must never change the save's outcome.
const safely = (step: string, run: () => void): void => {
  try {
    run();
  } catch (error) {
    logger.warn('[documentUpdateTracking] telemetry step failed', {
      step,
      errorName: error instanceof Error ? error.name : typeof error,
    });
  }
};

/**
 * Runs one autosave request as a `document_update` attempt. The request's
 * result or error passes through unchanged. Instanced, so overlapping saves
 * each report their own outcome.
 */
export const trackDocumentUpdateAttempt = async <T>(
  attempt: DocumentUpdateAttempt,
  save: () => Promise<T>
): Promise<T> => {
  let handle: TrackingHandle | undefined;
  safely('begin', () => {
    handle = startTracking({
      key: TrackingKey.SCRIBE_DOCUMENT_UPDATE,
      instanced: true,
      metadata: { editor: attempt.editor, trigger: 'autonomous' },
    });
  });

  let result: T;
  try {
    result = await save();
  } catch (error) {
    safely('fail', () => {
      if (!handle) return;
      const classified = classifyDocumentUpdateError(error);
      finishTracking({
        handle,
        outcome: 'failed',
        error: classified,
        metadata: {
          result: 'error',
          ...(classified.name === 'DocumentUpdateInterrupted'
            ? { cancel_reason: 'unauthorized' }
            : {}),
        },
      });
    });
    throw error;
  }

  // The legacy HTTP handler resolves a dead-session 401 or a suspended-account
  // 403 with no response, after a logout or redirect. Nothing was saved.
  if (result == null) {
    safely('interrupted', () => {
      if (!handle) return;
      finishTracking({
        handle,
        outcome: 'failed',
        error: namedError('DocumentUpdateInterrupted'),
        metadata: { result: 'error', cancel_reason: 'interrupted' },
      });
    });
    return result;
  }

  safely('complete', () => {
    if (!handle) return;
    finishTracking({ handle, metadata: { result: 'success' } });
  });
  return result;
};
